import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface PopupChatWindowProps {
  isOpen: boolean;
  onToggle: () => void;
  onAIResponse: (title: string, content: string) => void;
}

const ChatContainer = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 90px;
  right: 24px;
  width: 380px;
  height: 500px;
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  overflow: hidden;
  z-index: 1000;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  @media (max-width: 480px) {
    width: calc(100vw - 48px);
    right: 24px;
    left: 24px;
    height: 400px;
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, #4B0082, #6A0DAD);
  color: white;
  padding: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderControls = styled.div`
  display: flex;
  gap: 8px;
`;

const ControlButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 4px;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s ease;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: scale(1.1);
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const WelcomeMessage = styled.div`
  text-align: center;
  color: #666;
  font-size: 14px;
  line-height: 1.6;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 2px dashed #ddd;

  p {
    margin: 8px 0;
  }

  .emoji {
    font-size: 24px;
    margin-bottom: 8px;
  }
`;

const MessageWrapper = styled.div<{ $isUser: boolean }>`
  display: flex;
  flex-direction: column;
  max-width: 85%;
  align-self: ${props => props.$isUser ? 'flex-end' : 'flex-start'};
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  padding: 14px 18px;
  border-radius: 20px;
  font-size: 14px;
  line-height: 1.5;
  word-wrap: break-word;
  background: ${props => props.$isUser ? '#4B0082' : '#f1f3f5'};
  color: ${props => props.$isUser ? 'white' : '#333'};
  border-bottom-${props => props.$isUser ? 'right' : 'left'}-radius: 6px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const MessageTimestamp = styled.div<{ $isUser: boolean }>`
  font-size: 11px;
  color: #999;
  margin-top: 6px;
  padding: 0 6px;
  text-align: ${props => props.$isUser ? 'right' : 'left'};
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
  padding: 4px 0;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #999;
    animation: typing 1.4s infinite ease-in-out;
  }

  span:nth-child(1) { animation-delay: -0.32s; }
  span:nth-child(2) { animation-delay: -0.16s; }

  @keyframes typing {
    0%, 80%, 100% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const InputContainer = styled.div`
  padding: 20px;
  border-top: 1px solid #eee;
  display: flex;
  gap: 12px;
  align-items: flex-end;
  background: #fafbfc;
`;

const ChatInput = styled.textarea`
  flex: 1;
  border: 2px solid #e1e5e9;
  border-radius: 24px;
  padding: 14px 18px;
  font-size: 14px;
  resize: none;
  outline: none;
  font-family: inherit;
  transition: all 0.2s ease;
  background: white;

  &:focus {
    border-color: #4B0082;
    box-shadow: 0 0 0 3px rgba(75, 0, 130, 0.1);
  }

  &:disabled {
    background: #f8f9fa;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }
`;

const SendButton = styled.button`
  background: #4B0082;
  color: white;
  border: none;
  padding: 14px 16px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s ease;
  min-width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(75, 0, 130, 0.3);

  &:hover:not(:disabled) {
    background: #6A0DAD;
    transform: scale(1.05);
    box-shadow: 0 6px 16px rgba(75, 0, 130, 0.4);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const FloatingChatButton = styled.button<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 24px;
  right: 24px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4B0082, #6A0DAD);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 24px;
  display: ${props => props.$isOpen ? 'none' : 'flex'};
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(75, 0, 130, 0.4);
  transition: all 0.3s ease;
  z-index: 999;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 32px rgba(75, 0, 130, 0.5);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const PopupChatWindow: React.FC<PopupChatWindowProps> = ({ 
  isOpen, 
  onToggle, 
  onAIResponse 
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      // Use the team coordinator chat endpoint for documentation queries
      const response = await fetch('http://localhost:3001/api/team-coordinator/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage.content,
          context: 'documentation'
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Create assistant message
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: data.response || 'I\'ve generated the documentation for you.',
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Pass the AI response to the parent component for display
      if (onAIResponse && data.response) {
        onAIResponse(
          `Documentation: ${userMessage.content}`,
          data.response
        );
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: 'Sorry, there was an error processing your request. Please try again.',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const clearChat = () => {
    setMessages([]);
  };

  return (
    <>
      <FloatingChatButton $isOpen={isOpen} onClick={onToggle}>
        💬
      </FloatingChatButton>

      <ChatContainer $isOpen={isOpen}>
          <ChatHeader>
            <HeaderTitle>
              🤖 Documentation Assistant
            </HeaderTitle>
            <HeaderControls>
              <ControlButton
                onClick={clearChat}
                disabled={messages.length === 0}
                title="Clear Chat"
              >
                🗑️ Clear
              </ControlButton>
              <CloseButton onClick={onToggle} title="Close Chat">
                ✕
              </CloseButton>
            </HeaderControls>
          </ChatHeader>

          <MessagesContainer>
            {messages.length === 0 && (
              <WelcomeMessage>
                <div className="emoji">📚</div>
                <p><strong>Welcome to the Documentation Assistant!</strong></p>
                <p>Ask me about any application in our knowledge base:</p>
                <p>• "Show me the Cross-Sell MFE architecture"</p>
                <p>• "What are the key features of the Hardware Selection app?"</p>
                <p>• "Explain the UOST application workflow"</p>
              </WelcomeMessage>
            )}
            
            {messages.map((message) => (
              <MessageWrapper key={message.id} $isUser={message.role === 'user'}>
                <MessageBubble $isUser={message.role === 'user'}>
                  {message.content}
                </MessageBubble>
                <MessageTimestamp $isUser={message.role === 'user'}>
                  {message.timestamp.toLocaleTimeString()}
                </MessageTimestamp>
              </MessageWrapper>
            ))}
            
            {isLoading && (
              <MessageWrapper $isUser={false}>
                <MessageBubble $isUser={false}>
                  <TypingIndicator>
                    <span></span>
                    <span></span>
                    <span></span>
                  </TypingIndicator>
                </MessageBubble>
              </MessageWrapper>
            )}
            
            <div ref={messagesEndRef} />
          </MessagesContainer>

          <InputContainer>
            <ChatInput
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about our applications... (Press Enter to send)"
              rows={2}
              disabled={isLoading}
            />
            <SendButton
              onClick={sendMessage}
              disabled={!inputValue.trim() || isLoading}
            >
              {isLoading ? '⏳' : '📤'}
            </SendButton>
          </InputContainer>
      </ChatContainer>
    </>
  );
};

export default PopupChatWindow;
